import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Typography, Divider,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { ContentContainer } from '@/pages/PreOnboarding/steps/AccessCodeSpark/AccessCodeEntry.styles';
import {
  FlexContainer,
  HeaderBox,
  Title,
  continueButtonStyles,
  DividerContainer,
  DividerText,
} from '@/pages/PreOnboarding/steps/AccessCodeSpark/CreateAccount.styles';

import EmailIcon from '@/assets/Email.svg';
// import GoogleIcon from '@/assets/Google.svg';
// import AppleIcon from '@/assets/Apple.svg';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import SocialButton from '@/components/SocialButton/SocialButton';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';

const PHONE_ERROR_MESSAGE = 'coverage.sleepio.patient.please_enter_valid_phone_number';

const schema = z.object({
  phoneNumber: z.string().refine(
    (v) => isValidPhoneNumber(v || ''),
    { message: PHONE_ERROR_MESSAGE },
  ),
});

type LoginPhoneForm = z.infer<typeof schema>;

const LoginPhoneInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();
  const theme = useTheme();

  const history = useHistory();
  const { search } = useLocation();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const [redirectToError, setRedirectToError] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const methods = useForm<LoginPhoneForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { phoneNumber: '' },
  });
  const { handleSubmit, control, formState } = methods;

  useEffect(() => {
    posthog?.capture('SparkLoginPhoneScreenViewed');
  }, [posthog]);

  useEffect(() => {
    const redirectTo = params.get('redirectTo');
    if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
      setRedirectToError(true);
      return;
    }
    sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
  }, [params]);

  const onSubmit = async (data: LoginPhoneForm) => {
    posthog?.capture('SparkLoginPhoneContinueClicked');
    setIsSending(true);
    setSendError('');

    const { error: otpError } = await getSupabaseClient().auth.signInWithOtp({
      phone: data.phoneNumber,
    });

    if (otpError) {
      setSendError(t('coverage.pre_onboarding.create_account.error_send_failed'));
      setIsSending(false);
      return;
    }

    history.push('/activate/spark/sms/verify', { phoneNumber: data.phoneNumber, fromLogin: true });
  };

  if (redirectToError) {
    return (
      <ContentContainer>
        <FlexContainer maxWidth="xs">
          <Typography color="error" data-testid="redirect-to-error" style={{ marginTop: 32 }}>
            {t('coverage.pre_onboarding.access_code.redirect_error')}
          </Typography>
        </FlexContainer>
        <Footer />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer>
      <FlexContainer maxWidth="xs">
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.spark.login.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.spark.login.sub_title')}
          </Typography>
        </HeaderBox>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: t(PHONE_ERROR_MESSAGE),
                validate: (v) => isValidPhoneNumber(v || '') || t(PHONE_ERROR_MESSAGE),
              }}
              render={({ field, fieldState }) => (
                <>
                  <Typography
                    variant="body2"
                    style={{ color: theme.palette.secondary.main, fontWeight: 600, marginBottom: 4 }}
                  >
                    {t('coverage.sleepio.patient.phone_label')}
                  </Typography>
                  <PhoneNumberInput
                    id="phoneNumber"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={t('coverage.sleepio.patient.phone_label')}
                    error={!!fieldState.error}
                    helperText={fieldState.error ? t(PHONE_ERROR_MESSAGE) : undefined}
                    required
                  />
                </>
              )}
            />

            <Button
              variant="contained"
              data-testid="continue-button"
              type="submit"
              fullWidth
              color="primary"
              disabled={!formState.isValid || isSending}
              rounded
              style={continueButtonStyles}
            >
              {isSending
                ? t('coverage.pre_onboarding.create_account.sending')
                : t('login.login')}
            </Button>

            {sendError && (
              <Typography color="error" data-testid="send-error-message" style={{ marginTop: 8, marginBottom: 10 }}>
                {sendError}
              </Typography>
            )}

            <DividerContainer>
              <Divider />
              <DividerText>{t('coverage.pre_onboarding.create_account.or')}</DividerText>
            </DividerContainer>

            {/* TODO: Uncomment when Google/Apple social auth is implemented */}
            {/* <SocialButton
              icon={<img src={GoogleIcon} alt="Google icon" width={20} height={20} />}
              labelKey="coverage.pre_onboarding.create_account.continue_google"
              testId="continue-google-button"
              onClick={() => {}}
            />
            <SocialButton
              icon={<img src={AppleIcon} alt="Apple icon" width={20} height={20} />}
              labelKey="coverage.pre_onboarding.create_account.continue_apple"
              testId="continue-apple-button"
              onClick={() => {}}
            /> */}
            <SocialButton
              icon={<img src={EmailIcon} alt="Email icon" width={20} height={20} />}
              labelKey="coverage.pre_onboarding.create_account.continue_email"
              testId="continue-email-button"
              onClick={() => {}}
              marginBottom={0}
            />
          </form>
        </FormProvider>
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

const LoginPhone: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <LoginPhoneInner />
  </ThemeProvider>
);

export default LoginPhone;
